import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type MouseEvent,
} from 'react';
import { Share } from 'lucide-react';
import { useTracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import { SURFACE_HOVER } from '~/ui-rocco/button-system';
import {
    PLATFORMS_CONFIG,
    type Platform,
} from '@rocco/ui/social-sharing/utils/platform';
import {
    shareContent,
    type ShareOptions,
} from '~/modules/social-sharing/utils/sharing';
import { capitalizeFirstLetter } from '~/utils/stringHelper';
import {
    CONTROL_RADIUS,
    CONTROL_SIZE,
    GLYPH_INSET,
    LABEL_CLASS,
    SWEEP_EASING,
    useLabelledWidth,
} from './tile-action-control';

/** The name this control sweeps out, and what the sheet is called. */
const SHARE_LABEL = 'Share';

/**
 * The open box, stated rather than hugged: neither width nor height animates
 * to `auto`, so the panel is sized for its rows here. Three targets across and
 * two down — one row of six is the shape of the reference, but the box grows
 * inside a tile that clips it, and six abreast does not fit the narrow ones.
 */
export const SHARE_SHEET_WIDTH = '13.5rem';
export const SHARE_SHEET_HEIGHT = '8.5rem';

/**
 * The sheet's surface: white with a glass rim — bright along the top, dark
 * along the bottom — lifted off whatever it lies over. One definition, so the
 * sheet a tile's disc grows into and the one the photo viewer drops from its
 * Share pill are the same object.
 */
export const SHARE_SHEET_SURFACE = cn(
    'bg-white',
    '[box-shadow:inset_0_1px_1px_rgba(255,255,255,0.85),inset_0_-1px_1px_rgba(0,0,0,0.08),0_6px_24px_-6px_rgba(0,0,0,0.35)]',
);

/**
 * The open box overshoots, the way the detail page's menu does. Widening to
 * show a label does not: a spring on hover-in and hover-out across a grid of
 * tiles reads as twitch rather than as craft.
 */
const MORPH_EASING = 'cubic-bezier(0.3, 1.6, 0.5, 1)';

/** One target: the mark in a disc, its name underneath. */
const ShareTarget = ({
    icon,
    name,
    tabbable,
    onSelect,
}: {
    icon: React.ReactNode;
    name: string;
    tabbable: boolean;
    onSelect: () => void;
}) => (
    <button
        type="button"
        role="menuitem"
        tabIndex={tabbable ? 0 : -1}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect();
        }}
        className="group/target flex cursor-pointer flex-col items-center gap-1 rounded-lg bg-transparent p-1 transition-colors hover:bg-gray-100"
    >
        <span className="flex size-7 items-center justify-center rounded-[var(--radius-btn-xs)] bg-[#f0f0f0] text-[#181818] transition-colors group-hover/target:bg-[#e4e4e4] [&_svg]:size-3.5!">
            {icon}
        </span>
        <span className="text-center font-sans text-[0.625rem] leading-[1.2] font-medium whitespace-nowrap text-[#181818]">
            {name}
        </span>
    </button>
);

/**
 * The sheet's targets: three across, two down, each a mark in a disc with its
 * name under it. A target with no picture to pin cannot offer the pin.
 */
export const ShareTargets = ({
    share,
    open,
    onSelect,
    className,
}: {
    share: ShareOptions;
    open: boolean;
    onSelect: (platform: Platform) => void;
    className?: string;
}) => {
    const targets = PLATFORMS_CONFIG.filter(
        (target) => target.platform !== 'pinterest' || !!share.image,
    );
    return (
        <div
            role="menu"
            className={cn(
                'grid grid-cols-3 content-center gap-x-1 gap-y-1 p-2',
                className,
            )}
        >
            {targets.map((target) => (
                <ShareTarget
                    key={target.platform}
                    icon={target.icon}
                    name={target.displayName}
                    tabbable={open}
                    onSelect={() => onSelect(target.platform)}
                />
            ))}
        </div>
    );
};

/**
 * Closes the sheet on a click anywhere outside `wrapRef`, or on Escape. The
 * sheet is part of its host rather than a dismissable layer, so the host owns
 * this itself.
 */
export const useDismissShareSheet = (
    open: boolean,
    wrapRef: React.RefObject<HTMLElement | null>,
    onOpenChange: (open: boolean) => void,
) => {
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: PointerEvent) => {
            if (e.target instanceof Node && wrapRef.current?.contains(e.target))
                return;
            onOpenChange(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onOpenChange(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onOpenChange, wrapRef]);
};

/** Sends the share to its target, logs it, and shuts the sheet. */
export const useShareSelect = (
    share: ShareOptions,
    onOpenChange: (open: boolean) => void,
) => {
    const tracker = useTracker();
    return useCallback(
        (platform: Platform) => {
            onOpenChange(false);
            tracker.log('ShareSubmit', {
                url: new URL(window.location.href),
                data: { ExtraData: capitalizeFirstLetter(platform) },
            });
            void shareContent(platform, share);
        },
        [onOpenChange, share, tracker],
    );
};

/**
 * The tile's share sheet, which the More disc becomes rather than spawns.
 *
 * The disc grows into the panel on a curve that overshoots, clipping its own
 * contents on the way, and the targets fade in once the box has nearly
 * arrived — the same open the detail page's own menu uses, so the two behave
 * as one idea.
 *
 * In place rather than floating beside the tile: the animation is the disc
 * turning into the box, which only reads if the box starts where the disc is.
 * That means it has to fit inside the picture, since the tile clips it.
 */
export const TileShareMenu = ({
    share,
    open,
    onOpenChange,
    disabled,
}: {
    /** What is being shared, including the picture the pin needs. */
    share: ShareOptions;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    disabled?: boolean;
}) => {
    const tracker = useTracker();
    const wrapRef = useRef<HTMLDivElement>(null);
    // The label's sweep is driven from state rather than `group-hover`, because
    // the width it animates is an inline style — see the box below.
    const [labelled, setLabelled] = useState(false);
    // The same measurement the row's other controls use, so the label sits the
    // same distance off its glyph on every one of them.
    const { labelRef, labelledWidth } = useLabelledWidth(SHARE_LABEL);
    // Which curve the box is travelling on. Held on a ref rather than derived
    // from `open`, because closing and un-hovering both end at the shut disc
    // and only one of them should spring: read at render, and every setter
    // that changes it re-renders anyway.
    const easingRef = useRef(MORPH_EASING);

    useDismissShareSheet(open, wrapRef, onOpenChange);
    const handleSelect = useShareSelect(share, onOpenChange);

    const toggle = useCallback(
        (e: MouseEvent) => {
            // The panel sits inside the tile's own link.
            e.preventDefault();
            e.stopPropagation();
            easingRef.current = MORPH_EASING;
            if (!open) {
                tracker.log('ShareReveal', {
                    url: new URL(window.location.href),
                });
                // The name has done its job once the panel is arriving.
                setLabelled(false);
            }
            onOpenChange(!open);
        },
        [open, onOpenChange, tracker],
    );

    const sweepLabel = useCallback((next: boolean) => {
        easingRef.current = SWEEP_EASING;
        setLabelled(next);
    }, []);

    return (
        <div
            ref={wrapRef}
            className="pointer-events-auto relative"
            // The wrapper widens with the label but not with the panel.
            //
            // The box inside is absolute, so it grows leftwards out of here
            // rather than along the row. Left at one width that meant a
            // sweeping label opened straight over the control beside it — the
            // row is the same flex row, it just had nothing to reflow. Taking
            // the label's width here pushes its neighbours along instead.
            //
            // The open panel still overflows on purpose: it is far wider than
            // any row would give it, and it is meant to lie over the picture.
            style={{
                width: labelled && !open ? labelledWidth : CONTROL_SIZE,
                height: CONTROL_SIZE,
                transition: 'width 200ms',
                transitionTimingFunction: SWEEP_EASING,
            }}
        >
            <div
                className={cn(
                    'absolute right-0 bottom-0 overflow-hidden',
                    SHARE_SHEET_SURFACE,
                    'transition-[width,height,border-radius] duration-200',
                    'motion-reduce:transition-none',
                )}
                // Inline rather than utilities: as classes these lost the
                // cascade to the tile's own rules and the box never grew, which
                // is not a fight worth having for the numbers the animation is
                // defined by anyway.
                style={{
                    width: open
                        ? SHARE_SHEET_WIDTH
                        : labelled
                          ? labelledWidth
                          : CONTROL_SIZE,
                    height: open ? SHARE_SHEET_HEIGHT : CONTROL_SIZE,
                    // One corner in both states: the step's own. The box used
                    // to round off to a disc when shut, which read as a
                    // different family from the pills beside it.
                    borderRadius: CONTROL_RADIUS,
                    transitionTimingFunction: easingRef.current,
                }}
            >
                <button
                    type="button"
                    disabled={disabled}
                    aria-label="Share this, copy its link, or pin it"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={toggle}
                    onPointerEnter={() => sweepLabel(true)}
                    onPointerLeave={() => sweepLabel(false)}
                    onFocus={() => sweepLabel(true)}
                    onBlur={() => sweepLabel(false)}
                    className={cn(
                        // Pinned right, not centred: the box grows leftwards
                        // out of this corner, so centring would drift the glyph
                        // along with the edge instead of holding it still while
                        // the name arrives beside it.
                        'absolute inset-0 flex items-center justify-end text-gray-900',
                        // The dim sits on the face rather than on the box: the
                        // box is what animates open, and it has to still be
                        // white behind the share targets once it has.
                        SURFACE_HOVER,
                        'transition-[opacity,background-color] duration-150 disabled:opacity-60',
                        open ? 'pointer-events-none opacity-0' : 'opacity-100',
                    )}
                    style={{ paddingRight: GLYPH_INSET }}
                >
                    <span
                        className={cn(
                            // `shrink-0` for the reason the icon button gives:
                            // shrinkable, it loses the gap to its glyph.
                            'block max-w-0 shrink-0 overflow-hidden opacity-0',
                            'transition-all duration-200 ease-out',
                            'motion-reduce:transition-none',
                        )}
                        // Well past any label this carries: the box's own
                        // width is what bounds the sweep, and it is measured.
                        // A cap of its own here just clipped the label, and
                        // with it the gap to the glyph.
                        style={
                            labelled && !open
                                ? { maxWidth: '12rem', opacity: 1 }
                                : undefined
                        }
                    >
                        <span ref={labelRef} className={LABEL_CLASS}>
                            {SHARE_LABEL}
                        </span>
                    </span>
                    <Share className="size-4 shrink-0" />
                </button>

                {/* Behind the growing box, so it is clipped until the box is
                    big enough to hold it. */}
                <ShareTargets
                    share={share}
                    open={open}
                    onSelect={handleSelect}
                    className={cn(
                        'absolute inset-0 transition-opacity duration-300',
                        open
                            ? 'opacity-100 delay-150'
                            : 'pointer-events-none opacity-0',
                    )}
                />
            </div>
        </div>
    );
};
